import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useReportTemplates } from "@/api/hooks/useReportTemplates";
import { useReport, useRequestReport } from "@/api/hooks/useReports";
import { ReportDto, ReportStatus, ReportTemplateDto } from "@/api/types/dtos/report";
import { downloadReportFile } from "@/utils/reportDownload";

const STATUS_LABELS: Record<ReportStatus, string> = {
  requested: "Solicitado",
  in_process: "En proceso",
  available: "Disponible",
  failed: "Fallido",
};

const STATUS_COLORS: Record<ReportStatus, "default" | "info" | "success" | "error"> = {
  requested: "default",
  in_process: "info",
  available: "success",
  failed: "error",
};

interface ReportRequestDialogProps {
  open: boolean;
  onClose: () => void;
  /** Selected organizations to report on. */
  orgs: { id: string; name: string }[];
  /** Whether the org list is still being accumulated (e.g. select-all-matching). */
  orgsLoading?: boolean;
  /** Optional context label, e.g. "Retos" or the drill-down filter value. */
  sourceLabel?: string;
}

/**
 * Shared "request report from selected orgs" dialog.
 * Picks a template, submits POST /reports in MANUAL mode with the selected
 * org ids, polls status and offers download when available.
 */
export const ReportRequestDialog: React.FC<ReportRequestDialogProps> = ({
  open,
  onClose,
  orgs,
  orgsLoading = false,
  sourceLabel,
}) => {
  const { data: templates = [], isLoading: templatesLoading } = useReportTemplates();
  const requestReport = useRequestReport();

  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplateDto | null>(null);
  const [reportName, setReportName] = useState("");
  const [reportId, setReportId] = useState<string | null>(null);

  const { data: reportData, refetch: refetchReport } = useReport(reportId || "");

  // Reset state whenever the dialog is (re)opened
  useEffect(() => {
    if (open) {
      setSelectedTemplate(null);
      setReportName("");
      setReportId(null);
    }
  }, [open]);

  // Auto-generate report name when a template is selected
  useEffect(() => {
    if (selectedTemplate && !reportName) {
      setReportName(`${selectedTemplate.name} - ${new Date().toLocaleDateString("es")}`);
    }
  }, [selectedTemplate, reportName]);

  const report = reportData as ReportDto | undefined;
  const status = report?.status;
  const isProcessing =
    !!reportId && (status === undefined || status === "requested" || status === "in_process");

  // Poll while the report is being generated
  useEffect(() => {
    if (!reportId || status === "available" || status === "failed") return;
    const interval = setInterval(() => refetchReport(), 5000);
    return () => clearInterval(interval);
  }, [reportId, status, refetchReport]);

  const canSubmit = useMemo(
    () => !!selectedTemplate && orgs.length > 0 && !orgsLoading && !requestReport.isPending,
    [selectedTemplate, orgs.length, orgsLoading, requestReport.isPending]
  );

  const handleSubmit = async () => {
    if (!selectedTemplate || orgs.length === 0) return;
    try {
      const created = await requestReport.mutateAsync({
        reportTemplateId: selectedTemplate.id,
        name: reportName || `Reporte ${new Date().toLocaleDateString("es")}`,
        targetSelectionMode: "MANUAL",
        targetOrganizationIds: orgs.map((org) => org.id),
      });
      setReportId(created.id);
    } catch {
      // Error surfaced via requestReport.isError
    }
  };

  const handleDownload = () => {
    if (!report?.reportS3Url) return;
    downloadReportFile(report.reportS3Url, report.name || `reporte_${report.id}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Descargar Reporte</DialogTitle>
      <DialogContent>
        {!reportId ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Alert severity="info">
              <span>
                {orgsLoading
                  ? "Cargando organizaciones..."
                  : orgs.length === 1
                    ? `1 organización seleccionada${sourceLabel ? ` (${sourceLabel})` : ""}`
                    : `${orgs.length} organizaciones seleccionadas${sourceLabel ? ` (${sourceLabel})` : ""}`}
              </span>
            </Alert>

            <Autocomplete
              options={templates}
              getOptionLabel={(option) => `${option.name} (v${option.version})`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              loading={templatesLoading}
              value={selectedTemplate}
              onChange={(_, value) => setSelectedTemplate(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Plantilla de Reporte"
                  required
                  helperText={
                    !templates.length && !templatesLoading
                      ? "No hay plantillas disponibles. Crea una primero en Reportes."
                      : ""
                  }
                />
              )}
              noOptionsText="No hay plantillas disponibles"
            />

            <TextField
              fullWidth
              label="Nombre del Reporte"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder={`Reporte ${new Date().toLocaleDateString("es")}`}
            />

            {requestReport.isError && (
              <Alert severity="error">
                Error al solicitar el reporte. Verifica los datos e intenta nuevamente.
              </Alert>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1, textAlign: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
              <Typography variant="body2">Estado:</Typography>
              {status ? (
                <Chip label={STATUS_LABELS[status]} color={STATUS_COLORS[status]} size="small" />
              ) : (
                <CircularProgress size={20} />
              )}
            </Box>
            {isProcessing && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Generando reporte...
                </Typography>
              </Box>
            )}
            {status === "available" && (
              <>
                <Alert severity="success">Reporte generado exitosamente.</Alert>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Descargar CSV
                </Button>
              </>
            )}
            {status === "failed" && (
              <Alert severity="error">Error al generar el reporte. Intenta nuevamente.</Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{reportId ? "Cerrar" : "Cancelar"}</Button>
        {!reportId && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {requestReport.isPending ? <CircularProgress size={20} /> : "Generar Reporte"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportRequestDialog;
